/**
 * TenantChat — Tenant chat with file/image sharing.
 *
 * Single private conversation with the landlord, scoped by tenant_link_id.
 * Table: chat_messages.
 */

import { useEffect, useRef, useState } from 'react';
import { MessageSquare, Send, Loader2, Paperclip, FileText, X, Download } from 'lucide-react';
import { useAppContext } from '../../context/useAppContext';
import { supabase } from '../../lib/supabase';
import { createNotification } from '../../utils/notifications';
import { usePresenceFor } from '../../lib/presence';
import PresenceIndicator from '../../components/PresenceIndicator';

interface ChatMsg {
    id: string;
    tenant_link_id: string;
    property_id: string;
    sender_id: string;
    sender_role: 'landlord' | 'tenant';
    content: string;
    attachment_url: string | null;
    attachment_name: string | null;
    read_at: string | null;
    created_at: string;
}

export default function TenantChat() {
    const { user } = useAppContext();
    const [tenantLinkId, setTenantLinkId] = useState<string | null>(null);
    const [propertyId, setPropertyId] = useState<string | null>(null);
    const [landlordId, setLandlordId] = useState<string | null>(null);
    const [propertyName, setPropertyName] = useState('');
    const [messages, setMessages] = useState<ChatMsg[]>([]);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [noLink, setNoLink] = useState(false);

    // Live presence for the landlord on the other side of this conversation
    const landlordPresence = usePresenceFor([landlordId]);

    // Attachment
    const [attachFile, setAttachFile] = useState<File | null>(null);
    const [attachPreview, setAttachPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const bottomRef = useRef<HTMLDivElement>(null);
    const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

    // ── Bootstrap ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!user.id && !user.email) return;
        (async () => {
            let q = supabase.from('tenant_links').select('id, property_id').eq('invite_status', 'active');
            if (user.id) q = q.eq('tenant_user_id', user.id);
            else q = q.eq('tenant_email', user.email);

            const { data: link } = await q.maybeSingle();
            if (!link) { setNoLink(true); setLoading(false); return; }

            setTenantLinkId(link.id);
            setPropertyId(link.property_id);

            const { data: prop } = await supabase
                .from('properties')
                .select('name, owner_id')
                .eq('id', link.property_id)
                .single();
            if (prop) { setPropertyName(prop.name); setLandlordId(prop.owner_id); }

            const { data: msgs, error } = await supabase
                .from('chat_messages')
                .select('*')
                .eq('tenant_link_id', link.id)
                .order('created_at', { ascending: true });
            if (error) console.error('Load messages error:', error);
            setMessages(msgs ?? []);

            // Mark landlord messages as read
            await supabase
                .from('chat_messages')
                .update({ read_at: new Date().toISOString() })
                .eq('tenant_link_id', link.id)
                .eq('sender_role', 'landlord')
                .is('read_at', null);

            setLoading(false);

            // Realtime
            const channel = supabase
                .channel(`chat:tenant:${link.id}`)
                .on(
                    'postgres_changes',
                    { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `tenant_link_id=eq.${link.id}` },
                    (payload) => {
                        const msg = payload.new as ChatMsg;
                        setMessages(prev => [...prev, msg]);
                        if (msg.sender_role === 'landlord') {
                            supabase.from('chat_messages').update({ read_at: new Date().toISOString() }).eq('id', msg.id);
                        }
                    }
                )
                .subscribe();

            channelRef.current = channel;
        })();

        return () => {
            if (channelRef.current) supabase.removeChannel(channelRef.current);
        };
    }, [user.id, user.email]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // ── File picker ──────────────────────────────────────────────────────────
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAttachFile(file);
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (ev) => setAttachPreview(ev.target?.result as string);
            reader.readAsDataURL(file);
        } else {
            setAttachPreview(null);
        }
        e.target.value = '';
    };

    const clearAttach = () => { setAttachFile(null); setAttachPreview(null); };

    // ── Send ─────────────────────────────────────────────────────────────────
    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if ((!draft.trim() && !attachFile) || !tenantLinkId || !propertyId || !user.id) return;
        setSending(true);
        const content = draft.trim();
        setDraft('');

        let attachment_url: string | null = null;
        let attachment_name: string | null = null;

        if (attachFile) {
            const ext = attachFile.name.split('.').pop() ?? 'bin';
            const path = `${tenantLinkId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
            const { error: upErr } = await supabase.storage
                .from('chat-attachments')
                .upload(path, attachFile, { upsert: false });
            if (upErr) {
                console.error('Upload error:', upErr);
            } else {
                const { data: urlData } = supabase.storage.from('chat-attachments').getPublicUrl(path);
                attachment_url = urlData.publicUrl;
                attachment_name = attachFile.name;
            }
            clearAttach();
        }

        const { error } = await supabase.from('chat_messages').insert({
            tenant_link_id: tenantLinkId,
            property_id: propertyId,
            sender_id: user.id,
            sender_role: 'tenant',
            content: content || (attachment_name ? `Sent ${attachment_name}` : ''),
            attachment_url,
            attachment_name,
        });

        if (error) {
            console.error('Send error:', error);
            setDraft(content);
        } else if (landlordId) {
            await createNotification(
                landlordId,
                'new_message',
                'New message from tenant',
                (content || attachment_name || '').slice(0, 80),
                '/app/landlord/chat'
            );
        }
        setSending(false);
    };

    // ── Helpers ──────────────────────────────────────────────────────────────
    const formatTime = (iso: string) =>
        new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const formatDay = (iso: string) => {
        const d = new Date(iso);
        const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
        if (diff === 0) return 'Today';
        if (diff === 1) return 'Yesterday';
        return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };

    const isImage = (name: string | null) =>
        !!name && /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(name);

    const grouped: { date: string; msgs: ChatMsg[] }[] = [];
    for (const m of messages) {
        const day = formatDay(m.created_at);
        const last = grouped[grouped.length - 1];
        if (last?.date === day) last.msgs.push(m);
        else grouped.push({ date: day, msgs: [m] });
    }

    // ── Render ───────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <Loader2 className="animate-spin" size={40} color="var(--color-primary)" />
            </div>
        );
    }

    if (noLink) {
        return (
            <div className="page" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                <MessageSquare size={40} color="var(--color-text-tertiary)" style={{ margin: '0 auto 1.5rem', display: 'block' }} />
                <h2 style={{ margin: '0 0 0.75rem' }}>No property linked</h2>
                <p style={{ color: 'var(--color-text-secondary)', maxWidth: 380, marginInline: 'auto', lineHeight: 1.6 }}>
                    You're not linked to a property yet. Contact your landlord.
                </p>
            </div>
        );
    }

    return (
        <div className="page chat-page chat-page--single">
            {/* Header */}
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: 2 }}>
                    <div style={{ fontWeight: 800, color: '#1a1d2e', fontSize: '1.0625rem' }}>Your Landlord</div>
                    {landlordId && (
                        <PresenceIndicator
                            lastSeen={landlordPresence.get(landlordId)}
                            variant="full"
                        />
                    )}
                </div>
                <div style={{ fontSize: '0.8125rem', color: '#94a3b8', fontWeight: 500 }}>{propertyName}</div>
            </div>

            {/* Messages */}
            <div className="chat-page__messages">
                {messages.length === 0 ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.9375rem', fontWeight: 500 }}>
                        No messages yet — send one to your landlord!
                    </div>
                ) : (
                    grouped.map(group => (
                        <div key={group.date}>
                            <div style={{ textAlign: 'center', margin: '1rem 0 0.75rem', fontSize: '0.75rem', fontWeight: 700, color: '#cbd5e1', letterSpacing: '0.04em' }}>
                                {group.date}
                            </div>
                            {group.msgs.map(m => {
                                const isMe = m.sender_role === 'tenant';
                                return (
                                    <div key={m.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: '8px' }}>
                                        <div style={{
                                            maxWidth: '68%', padding: '0.625rem 1rem',
                                            borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                            background: isMe ? '#6366f1' : '#f1f5f9',
                                            color: isMe ? '#fff' : '#1a1d2e',
                                            fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5, wordBreak: 'break-word',
                                        }}>
                                            {/* Attachment */}
                                            {m.attachment_url && (
                                                isImage(m.attachment_name) ? (
                                                    <a href={m.attachment_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: m.content ? '6px' : 0 }}>
                                                        <img src={m.attachment_url} alt={m.attachment_name ?? 'image'} style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 10, display: 'block' }} />
                                                    </a>
                                                ) : (
                                                    <a
                                                        href={m.attachment_url} target="_blank" rel="noopener noreferrer"
                                                        style={{
                                                            display: 'flex', alignItems: 'center', gap: '8px',
                                                            padding: '8px 12px', borderRadius: 10, marginBottom: m.content ? '6px' : 0,
                                                            background: isMe ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.04)',
                                                            textDecoration: 'none', color: 'inherit', fontSize: '0.8125rem', fontWeight: 600,
                                                        }}
                                                    >
                                                        <FileText size={16} style={{ flexShrink: 0 }} />
                                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {m.attachment_name ?? 'File'}
                                                        </span>
                                                        <Download size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
                                                    </a>
                                                )
                                            )}
                                            {m.content && !m.content.startsWith('Sent ') && m.content}
                                            {m.content && m.content.startsWith('Sent ') && !m.attachment_url && m.content}
                                            <div style={{ fontSize: '0.7rem', marginTop: '3px', opacity: 0.6, textAlign: 'right' }}>
                                                {formatTime(m.created_at)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))
                )}
                <div ref={bottomRef} style={{ height: 8 }} />
            </div>

            {/* Attachment preview */}
            {attachFile && (
                <div className="chat-page__attach-preview">
                    {attachPreview ? (
                        <img src={attachPreview} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                    ) : (
                        <div style={{ width: 48, height: 48, borderRadius: 8, background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FileText size={20} color="#64748b" />
                        </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#1a1d2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {attachFile.name}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{(attachFile.size / 1024).toFixed(0)} KB</div>
                    </div>
                    <button onClick={clearAttach} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}>
                        <X size={18} />
                    </button>
                </div>
            )}

            {/* Input */}
            <form onSubmit={handleSend} className="chat-page__input-bar">
                <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.csv" style={{ display: 'none' }} onChange={handleFileChange} />

                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach file or image"
                    style={{ padding: '0.625rem', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer', display: 'flex', flexShrink: 0 }}
                >
                    <Paperclip size={18} />
                </button>

                <input
                    type="text"
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    placeholder="Message your landlord…"
                    style={{ flex: 1, padding: '0.75rem 1.125rem', borderRadius: '14px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '0.9375rem', fontWeight: 500, color: '#1a1d2e', outline: 'none' }}
                    onFocus={e => (e.target.style.borderColor = '#6366f1')}
                    onBlur={e => (e.target.style.borderColor = '#e2e8f0')}
                />

                <button
                    type="submit"
                    disabled={(!draft.trim() && !attachFile) || sending}
                    style={{
                        padding: '0.75rem 1.25rem', borderRadius: '14px', border: 'none', background: '#6366f1',
                        color: '#fff', cursor: (draft.trim() || attachFile) ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700, fontSize: '0.9375rem',
                        opacity: (!draft.trim() && !attachFile) ? 0.45 : 1, transition: 'opacity 0.15s', flexShrink: 0,
                    }}
                >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
            </form>
        </div>
    );
}
